import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BoxArrowRight, Megaphone, Wallet2, QuestionCircle, ListCheck, PersonBadge } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { useAuth } from '../auth/AuthContext';
import PortalShell, { usePortalTheme } from '../components/PortalShell';
import ApplicationForm from '../components/ApplicationForm';
import TasksTab from '../components/TasksTab';
import BriefsTab from '../components/BriefsTab';
import PayoutsTab from '../components/PayoutsTab';
import HowItWorksModal from '../components/HowItWorksModal';
import { fetchBriefs, fetchCreatorMe } from '../api/creators';
import './Dashboard.scss';

// A creator's pages, each a route of its own: /dashboard/tasks … The bare
// /dashboard is the first one (or the application, before approval).
const CREATOR_TABS = ['tasks', 'briefs', 'payouts'];

export default function Dashboard() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  // Called up here, not in the shell, so the loading screens are themed too.
  const theme = usePortalTheme();

  // One `/dashboard/*` route renders this component for every page (App.js),
  // so it stays mounted — and keeps the fetched status — while pages change.
  const tabParam = (useParams()['*'] || '').split('/')[0] || null;
  const tab = CREATOR_TABS.includes(tabParam) ? tabParam : 'tasks';
  const [howOpen, setHowOpen] = useState(false);
  // Bumped by the Refresh button to remount the open page, so it re-fetches.
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Creator status from GET /creators/me ─────────────────────────────────
  //   'none' | 'rejected' → application form
  //   'pending'           → "under review" state (rendered by ApplicationForm)
  //   'approved'          → the dashboard (Tasks / Briefs / Payouts)
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(false);

  // Local-dev escape hatch: ?status=none|pending|rejected|approved forces the
  // creator status so the application form is reachable without touching the
  // real profile on the API. Ignored in production builds. Read once: the
  // menu links drop the query string, and the override must outlive them.
  const [statusOverride] = useState(() =>
    process.env.NODE_ENV === 'development'
      ? new URLSearchParams(window.location.search).get('status')
      : null
  );

  // `silent` (the Refresh button) keeps the current screen up while it asks,
  // and keeps it on a failed request rather than swapping in the error page.
  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (statusOverride) {
      setStatus(statusOverride);
      setStatusError(false);
      setStatusLoading(false);
      return;
    }
    if (!silent) {
      setStatusLoading(true);
      setStatusError(false);
    }
    try {
      const me = await fetchCreatorMe();
      setStatus(me?.status || 'none');
    } catch {
      if (!silent) setStatusError(true);
    } finally {
      setStatusLoading(false);
    }
  }, [statusOverride]);

  // Route guard — mirror the partner portal.
  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [loading, user, navigate]);

  // Fetch the creator status once we have an authenticated user.
  useEffect(() => {
    if (!loading && user) loadStatus();
  }, [loading, user, loadStatus]);

  const isCreator = status === 'approved';

  const [badges, setBadges] = useState({});

  useEffect(() => {
    if (!isCreator) return;
    let cancelled = false;
    fetchBriefs()
      .then((briefs) => {
        if (cancelled) return;
        const count = (state) => briefs.filter((b) => (b.creatorState || 'open') === state).length;
        setBadges({ tasks: count('confirmed'), briefs: count('open') });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isCreator, tab, refreshKey]);

  // A page that isn't there — before approval every page is, or a typo — goes
  // back to the bare dashboard, with the URL to match.
  useEffect(() => {
    if (statusLoading || statusError || !tabParam) return;
    if (!isCreator || !CREATOR_TABS.includes(tabParam)) navigate('/dashboard', { replace: true });
  }, [statusLoading, statusError, tabParam, isCreator, navigate]);

  // The Refresh button: re-fetch the creator status (an application may have
  // been approved meanwhile) and remount the open page so it loads again — in
  // place, on the same page.
  async function handleRefresh() {
    await loadStatus({ silent: true });
    setRefreshKey((k) => k + 1);
  }

  function handleLogout() {
    logout();
    navigate('/', { replace: true });
  }

  if (loading || !user || statusLoading) {
    return (
      <div className="dash-loading">
        <span>{t('shell.loading')}</span>
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="dash-loading">
        <span>{t('shell.error')}</span>
        <button className="btn-o btn-o--dark btn-o--sm" onClick={() => loadStatus()}>
          {t('shell.retry')}
        </button>
      </div>
    );
  }

  const isPending = status === 'pending';

  // Until the application is approved the only page is the application itself.
  const navItems = isCreator
    ? [
        { key: 'tasks', label: t('tab.tasks'), Icon: ListCheck, to: '/dashboard/tasks', badge: badges.tasks },
        { key: 'briefs', label: t('tab.briefs'), Icon: Megaphone, to: '/dashboard/briefs', badge: badges.briefs },
        { key: 'payouts', label: t('tab.payouts'), Icon: Wallet2, to: '/dashboard/payouts' },
      ]
    : [{ key: 'application', label: t('tab.application'), Icon: PersonBadge, to: '/dashboard' }];

  return (
    <PortalShell
      items={navItems}
      active={isCreator ? tab : 'application'}
      onRefresh={handleRefresh}
      theme={theme}
      actions={[
        { key: 'how', label: t('nav.how'), Icon: QuestionCircle, onClick: () => setHowOpen(true) },
        { key: 'logout', label: t('shell.logout'), Icon: BoxArrowRight, onClick: handleLogout, danger: true },
      ]}
    >
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />

      <div className="dash__main container-x">
        {!isCreator ? (
          // Not remounted on Refresh: it loads nothing itself, and a remount
          // would throw away whatever the creator has typed so far.
          <ApplicationForm
            status={status}
            pending={isPending}
            onApplied={() => loadStatus()}
          />
        ) : (
          <div className="creator-dash" key={refreshKey}>
            {tab === 'tasks' && <TasksTab onBrowseBriefs={() => navigate('/dashboard/briefs')} />}
            {tab === 'briefs' && <BriefsTab />}
            {tab === 'payouts' && <PayoutsTab />}
          </div>
        )}
      </div>
    </PortalShell>
  );
}
